import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send + receive HttpOnly session cookies
});

function readCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : "";
}

/** True when the browser has evidence of an active session (JS-readable
 *  `sl_csrf` cookie, set alongside the HttpOnly access token on login). */
export function hasSessionHint() {
  return Boolean(readCookie("sl_csrf"));
}

// Add CSRF header to every mutating request (double-submit cookie).
api.interceptors.request.use((config) => {
  const m = (config.method || "get").toUpperCase();
  if (m !== "GET" && m !== "HEAD" && m !== "OPTIONS") {
    const csrf = readCookie("sl_csrf");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN    = 403;

// Small helper — swallow duplicate toasts within a short window.
let _lastToastAt = 0;
function throttledToast(kind, msg) {
  const now = Date.now();
  if (now - _lastToastAt < 1500) return;
  _lastToastAt = now;
  (toast[kind] || toast.error)(msg);
}

// Redirect to /login exactly once after a confirmed session-death.
let _redirecting = false;
function redirectToLogin() {
  if (_redirecting) return;
  _redirecting = true;
  // Small delay so the toast is visible before navigation.
  setTimeout(() => {
    const { pathname, search } = window.location;
    if (pathname !== "/login") {
      const back = encodeURIComponent(pathname + search);
      window.location.assign(`/login?next=${back}`);
    }
    _redirecting = false;
  }, 400);
}

// Auto-refresh on 401 (except for the auth calls themselves).
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = original.url || "";
    const isAuthCall = url.includes("/auth/");

    // ─── 401 handling ────────────────────────────────────────────────
    if (status === HTTP_UNAUTHORIZED) {
      // Never retry auth calls themselves; and if we have no session
      // hint at all, don't bother — the request was made without a
      // session and refresh cannot succeed.
      if (isAuthCall || !hasSessionHint() || original._retry) {
        return Promise.reject(err);
      }
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh", {});
        await refreshing;
        refreshing = null;
        const csrf = readCookie("sl_csrf");
        if (csrf) original.headers["X-CSRF-Token"] = csrf;
        return api(original);
      } catch (e) {
        refreshing = null;
        // Refresh failed → the session is genuinely dead.
        throttledToast("error", "Session expired. Please sign in again.");
        redirectToLogin();
      }
    }

    // ─── 403 handling ────────────────────────────────────────────────
    if (status === HTTP_FORBIDDEN) {
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === "string" && detail) ||
                  "You don't have permission to perform this action.";
      throttledToast("error", msg);
    }

    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const status = err?.response?.status;
  const d = err?.response?.data?.detail;
  // Prefer a message the API sent; otherwise translate common HTTP codes
  // into human-friendly copy so users never see a raw "Request failed with
  // status code 4xx" toast (misread by end users as a 404).
  if (d != null) {
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    if (typeof d === "object" && d.msg) return d.msg;
    return String(d);
  }
  if (status === 401) return "Incorrect email or password.";
  if (status === 403) return "You don't have permission to perform this action.";
  if (status === 404) return "That resource is no longer available.";
  if (status === 409) return "That record already exists.";
  if (status === 422) return "Please check the highlighted fields and try again.";
  if (status === 429) return "Too many attempts — please wait a minute and try again.";
  if (status >= 500)  return "Our server had a hiccup. Please try again in a moment.";
  return err?.message || "Something went wrong. Please try again.";
}

export default api;
